import React from "react";
import {Container, Typography, Box, Alert} from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import {useClientQueries, useNotification} from "shared-lib";
import NewServiceRequestForm from "../components/NewServiceRequestForm";
import {useClientContext} from "../context/ClientContextProvider";

const NewServiceRequestPage: React.FC = () => {
  const navigate = useNavigate();
  const {clientId} = useParams<{clientId: string}>();
  const {showNotification} = useNotification();
  const {currentProperty} = useClientContext();

  const {useClientById} = useClientQueries();
  const {data: client, isLoading, error} = useClientById(clientId || "");

  const handleSuccess = () => {
    showNotification({
      message: "Service request successfully created! Redirecting...",
      severity: "success",
      autoHideDuration: 2000,
    });

    // Redirect after showing success message for 2 seconds
    setTimeout(() => {
      navigate(`/clients/${clientId}/requests`);
    }, 2000);
  };

  const handleError = (errorMessage: string) => {
    showNotification({
      message: errorMessage,
      severity: "error",
      autoHideDuration: 6000,
    });

    // Scroll to top to make sure error is visible
    window.scrollTo(0, 0);
  };

  if (isLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{my: 4}}>
          <Typography variant="h4">Loading...</Typography>
        </Box>
      </Container>
    );
  }

  if (error || !client) {
    showNotification({
      message: "Error loading client data. Please try again.",
      severity: "error",
    });

    return (
      <Container maxWidth="md">
        <Box sx={{my: 4}}>
          <Typography variant="h4" component="h1" gutterBottom>
            Error Loading Client
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{mb: 2}}>
      {!clientId && (
        <Alert severity="warning" sx={{mb: 3}}>
          Please log in to submit a service request.
        </Alert>
      )}

      {clientId && (
        <NewServiceRequestForm
          clientId={clientId || ""}
          onSuccess={handleSuccess}
          onError={handleError}
          propertyData={currentProperty}
        />
      )}
    </Box>
  );
};

export default NewServiceRequestPage;
